/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useMemo, useState } from 'react';
import {
  Typography,
  MuiTable,
  Box,
  IconButton,
  Icon,
  Collapse,
  FormDialog,
  Card,
  MuiBox,
  color
} from '@kis-technologies/kis-ui';
import { formatOrderSummary, getSummary } from '../types';
import { useStyles } from '../styled';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  taxAmount: number;
  data: any;
}

export const OrderSummaryOverlay: React.FC<Props> = ({ isOpen, onClose, data, taxAmount }) => {
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  const classes = useStyles()();

  const formattedData = useMemo(() => formatOrderSummary(data), [data]);

  const toggleRow = (idx: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [idx]: !prev[idx]
    }));
  };

  if (!isOpen) return null;

  const summary = getSummary(formattedData, taxAmount);

  return (
    <FormDialog open={isOpen} confirmButtonAction={onClose} confirmButtonText="Close" title="Order Summary">
      <MuiBox className={classes.deliverylogOrderOuterBox}>
        <MuiBox>
          <Card hasBorderRadius raised fullHeight>
            <Box flexOverride="1">
              <MuiTable.TableContainer className={classes.deliveryTableContainer} id="deliveryLogTable">
                <MuiTable.Table>
                  {/* HEADER */}
                  <MuiTable.TableHead sx={{ backgroundColor: '#fafafa' }}>
                    <MuiTable.TableRow>
                      <MuiTable.TableCell width="5%" />
                      <MuiTable.TableCell className={classes.deliverylogHeaders} sx={{ fontWeight: 600, width: '35%' }}>
                        Item
                      </MuiTable.TableCell>
                      <MuiTable.TableCell className={classes.deliverylogHeaders} sx={{ fontWeight: 600, width: '30%' }}>
                        Quantity
                      </MuiTable.TableCell>
                      <MuiTable.TableCell
                        align="right"
                        className={classes.deliverylogHeaders}
                        sx={{ fontWeight: 600, width: '30%' }}
                      >
                        Price
                      </MuiTable.TableCell>
                    </MuiTable.TableRow>
                  </MuiTable.TableHead>

                  {/* BODY */}
                  <MuiTable.TableBody>
                    {formattedData.map((product: any, idx: number) => {
                      const isRowOpen = expandedRows[idx];

                      const total = product.items.reduce(
                        (sum: number, i: any) => sum + Number(i.price || 0) * i.quantity,
                        0
                      );

                      return (
                        <React.Fragment key={idx}>
                          {/* PARENT ROW */}
                          <MuiTable.TableRow onClick={() => toggleRow(idx)} hover>
                            <MuiTable.TableCell>
                              <IconButton
                                sx={{
                                  border: `2px solid ${color.PRIMARY}`,
                                  borderRadius: '50%',
                                  color: color.PRIMARY,
                                  height: '28px',
                                  width: '28px'
                                }}
                              >
                                <Icon name={isRowOpen ? 'KeyboardArrowUp' : 'KeyboardArrowDown'} />
                              </IconButton>
                            </MuiTable.TableCell>

                            <MuiTable.TableCell>{product.productName}</MuiTable.TableCell>

                            <MuiTable.TableCell>{product.quantity}</MuiTable.TableCell>

                            <MuiTable.TableCell align="right">${total.toFixed(2)}</MuiTable.TableCell>
                          </MuiTable.TableRow>

                          {/* CHILD ROWS */}
                          {isRowOpen && (
                            <MuiTable.TableRow>
                              <MuiTable.TableCell colSpan={4} sx={{ backgroundColor: '#fafafa', p: 0, width: '100%' }}>
                                <Collapse in={isRowOpen}>
                                  <MuiTable.Table>
                                    <MuiTable.TableBody>
                                      {product.items.map((item: any, i: number) => (
                                        <MuiTable.TableRow key={i}>
                                          {/* Empty cell for icon alignment */}
                                          <MuiTable.TableCell width="5%">
                                            <IconButton sx={{ width: '28px', height: '28px' }} />
                                          </MuiTable.TableCell>

                                          {/* Name */}
                                          <MuiTable.TableCell sx={{ width: '35%' }}>
                                            {item.priceTypeName}
                                          </MuiTable.TableCell>

                                          {/* Quantity */}
                                          <MuiTable.TableCell sx={{ width: '30%' }}>{item.quantity}</MuiTable.TableCell>

                                          {/* Price */}
                                          <MuiTable.TableCell align="right" sx={{ width: '30%' }}>
                                            ${Number(item.price || 0).toFixed(2)}
                                          </MuiTable.TableCell>
                                        </MuiTable.TableRow>
                                      ))}
                                    </MuiTable.TableBody>
                                  </MuiTable.Table>
                                </Collapse>
                              </MuiTable.TableCell>
                            </MuiTable.TableRow>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </MuiTable.TableBody>
                </MuiTable.Table>
              </MuiTable.TableContainer>
            </Box>
          </Card>
        </MuiBox>

        {/* FOOTER */}
        <Box display="flex" justifyContent="space-between" px={3} py={2}>
          <Typography>Items: {summary.itemsCount}</Typography>

          <Box textAlign="right">
            {summary.taxes > 0 && <Typography>Taxes: ${summary.taxes.toFixed(2)}</Typography>}
            <Typography>Total: ${summary.total.toFixed(2)}</Typography>
          </Box>
        </Box>
      </MuiBox>
    </FormDialog>
  );
};
